import { generateGoogleMapsDirectionsUrl, httpsUrl } from '@/lib/utils';
import { getErrorCode } from '@/lib/error-utils';
import type { Tenant, SocialLinks } from '@/state/tenant';
import type { AppearanceSettings } from '@/state/appearance-settings';

// Keyed on the tenant id, never on the host label or the slug. MokoBoko is
// reached on its own domain, where the first label is not a slug at all, and a
// studio can change its slug, so a slug check drops MokoBoko's metadata after a
// rename. The id comes from MOKOBOKO_TENANT_ID (env-server.ts); unset, no studio
// matches.
export const isMokobokoTenant = (
  tenant: Pick<Tenant, 'tenant_id'> | undefined,
  mokobokoTenantId: string | undefined
) => !!mokobokoTenantId && tenant?.tenant_id === mokobokoTenantId;

// Off the document root on purpose: a file at /favicon.ico would be every
// studio's icon. That path answers per host instead (app/favicon.ico).
const MOKOBOKO_ICON_PATH = '/mokoboko-favicon.ico';
export const PLATFORM_ICON_PATH = '/timitomi-favicon.ico';

// The studio's upload over MokoBoko's own mark, null for neither. https only,
// since /favicon.ico sends it as a Location and the site pages link it.
export const studioIconPath = (
  tenant: Pick<Tenant, 'tenant_id'>,
  seo: AppearanceSettings['seo'] | undefined,
  mokobokoTenantId: string | undefined
) =>
  httpsUrl(seo?.favicon) ??
  (isMokobokoTenant(tenant, mokobokoTenantId) ? MOKOBOKO_ICON_PATH : null);

export type BookingPageStatus = 'available' | 'not-live' | 'suspended';

// Wider than BookingPageStatus on purpose: that one is what the tenant record
// says about the studio, and a studio can be perfectly live and still be
// unreachable because the proxy cannot get to the backend. Only this wider
// answer reaches a screen, so an outage can never read as a missing product.
export type SiteStatus = BookingPageStatus | 'unreachable';

export type UnavailableBookingPageStatus = Exclude<SiteStatus, 'available'>;

// Suspension stays unexplained: the visitor is not the person who can lift it,
// and the reason is the studio's business. Shared by the site gate and the
// embed so a dark studio reads the same on its own page and on a host's.
export const BOOKING_PAGE_UNAVAILABLE_COPY: Record<
  UnavailableBookingPageStatus,
  { title: string; description: string }
> = {
  'not-live': {
    title: 'This booking page\nis getting ready',
    description: 'The studio has not opened bookings yet.\nCheck back soon.',
  },
  suspended: {
    title: 'This booking page is paused',
    description: 'Bookings are not available right now. Try again later.',
  },
  unreachable: {
    title: "We can't reach the\nstudio's booking system",
    description: 'Try again in a few minutes.',
  },
};

// The public-route guard fronts every /public endpoint, so a tenant request
// failing with one of these is the whole booking page being dark, not one
// screen failing.
export const getBookingPageStatus = (error: unknown): BookingPageStatus => {
  const code = getErrorCode(error);
  if (code === 'TENANT_NOT_LIVE') return 'not-live';
  if (code === 'TENANT_SUSPENDED') return 'suspended';
  return 'available';
};

// Postcode shapes, longest first: US ZIP+4, PT, PL, SE/CZ/GR, NL, UK, CA, IE
// Eircode, then plain 4 to 6 digits, which read as a postcode only before the
// town ("10119 Berlin") or after a state ("TX 78701"), so a unit number such as
// "Suite 1500" stays.
const POSTAL_CODE = new RegExp(
  `(?<![\\w-])(?:${[
    '\\d{5}-\\d{4}',
    '\\d{4}-\\d{3}',
    '\\d{2}-\\d{3}',
    '\\d{3} \\d{2}',
    '\\d{4} ?[A-Z]{2}',
    '[A-Z]{1,2}\\d[A-Z\\d]? ?\\d[A-Z]{2}',
    '[A-Z]\\d[A-Z] ?\\d[A-Z]\\d',
    '[A-Z]\\d{2} ?[A-Z\\d]{4}',
    '\\d{4,6}(?=\\s+\\p{L}{2})',
    '(?<=\\b[A-Z]{2,3}\\s)\\d{4,6}',
  ].join('|')})(?![\\w-])`,
  'gu'
);

// Only the town part is read: the last part, or the one before a trailing
// country. Every part before it can hold a house or unit number that looks like
// a postcode ("Suite 200, 1234 Main St", "STE 1500"), and the first part is
// never read.
export const withoutPostalCode = (address: string) => {
  const parts = address.split(',').map((part) => part.trim());
  const last = parts.length - 1;
  const town = /\d/.test(parts[last]) ? last : last - 1;
  if (town < 1) return address;
  parts[town] = parts[town]
    .replace(POSTAL_CODE, '')
    .replace(/\s+/g, ' ')
    .trim();
  return parts.filter(Boolean).join(', ');
};

// What a person reads: the studio's own wording wins, because it carries
// details Google drops ("2nd floor", "back entrance"). Directions and the map
// read the full address; the postcode is noise on the page.
export const getTenantAddress = (
  tenant: Tenant | undefined,
  fallback?: string | null
) => {
  const address =
    tenant?.address?.trim() ||
    tenant?.location_config?.formatted_address?.trim() ||
    fallback;
  return address ? withoutPostalCode(address) : null;
};

// What we hand back to Google: the string Google itself returned wins, because
// the studio's own wording often does not resolve to a place.
const getTenantGeocodeAddress = (
  tenant: Tenant | undefined,
  fallback?: string | null
) =>
  tenant?.location_config?.formatted_address?.trim() ||
  tenant?.address?.trim() ||
  fallback ||
  null;

export const getTenantMapQuery = (tenant: Tenant | undefined) => {
  const placeId = tenant?.location_config?.place_id;
  return placeId ? `place_id:${placeId}` : getTenantGeocodeAddress(tenant);
};

export const getTenantDirectionsUrl = (
  tenant: Tenant | undefined,
  fallback?: string | null
) => {
  const config = tenant?.location_config;
  return generateGoogleMapsDirectionsUrl({
    placeId: config?.place_id,
    latitude: config?.latitude,
    longitude: config?.longitude,
    address: getTenantGeocodeAddress(tenant, fallback),
  });
};

// The digits that go before the number: '' when the studio stored the country
// code inside the number as well, which would otherwise dial and print +351
// twice, null when there is no code or no number. The backend stores the code
// as "+351", so only its digits are kept.
const dialCodeOf = (tenant: Tenant | undefined) => {
  const dial = tenant?.business_phone_country_code?.replace(/\D/g, '');
  const digits = tenant?.business_phone_number?.replace(/\D/g, '');
  if (!dial || !digits) return null;
  return digits.startsWith(dial) ? '' : dial;
};

export const getTenantPhoneHref = (tenant: Tenant | undefined) => {
  const digits = tenant?.business_phone_number?.replace(/\D/g, '');
  if (!digits) return null;
  return `tel:+${dialCodeOf(tenant) ?? ''}${digits}`;
};

export type TenantSocialLink = SocialLinks & { url: string };

export type TenantContactInfo = {
  socialLinks: TenantSocialLink[];
  email: string | null;
  phone: { label: string; href: string } | null;
  location: { label: string; href: string } | null;
  hasDetails: boolean;
};

const hasUrl = (link: SocialLinks): link is TenantSocialLink =>
  !!link.url?.trim();

export const getTenantContactInfo = (
  tenant: Tenant,
  general: AppearanceSettings['general'] | undefined
): TenantContactInfo => {
  const socialLinks = general?.show_social_profiles
    ? (tenant.social_links ?? [])
        .filter(hasUrl)
        .sort((a, b) => a.order - b.order)
    : [];

  const email = tenant.support_email || null;

  const phoneHref = getTenantPhoneHref(tenant);
  const dial = dialCodeOf(tenant);
  const number = tenant.business_phone_number ?? '';
  const phone =
    phoneHref && dial != null
      ? {
          label: dial
            ? `+${dial} ${number}`
            : `+${number.replace(/^\s*\+?/, '')}`,
          href: phoneHref,
        }
      : null;

  const address = getTenantAddress(tenant);
  const mapsUrl = getTenantDirectionsUrl(tenant);
  const location =
    address && mapsUrl ? { label: address, href: mapsUrl } : null;

  return {
    socialLinks,
    email,
    phone,
    location,
    hasDetails: socialLinks.length > 0 || !!email || !!phone || !!location,
  };
};

// The header shows the social chips on their own row, always, since the admin
// has a toggle for them; the contact chips keep their place inside the
// expanded About text.
export const splitTenantContactInfo = (contact: TenantContactInfo) => ({
  social: {
    ...contact,
    email: null,
    phone: null,
    location: null,
    hasDetails: contact.socialLinks.length > 0,
  },
  details: {
    ...contact,
    socialLinks: [],
    hasDetails: !!contact.email || !!contact.phone || !!contact.location,
  },
});
